import React from 'react';
import { ShowerHead, Beaker, Check, RotateCcw, AlertTriangle } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели с рекомендациями по обслуживанию воды
 */
const WaterMaintenanceSidebar = ({ 
  formData, 
  results,
  waterTypes,
  filterSystems
}) => {
  if (!results) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <ShowerHead className="w-5 h-5 mr-2 text-cyan-500" />
          Обслуживание воды
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь появятся рекомендации по химии и уходу за бассейном после выполнения расчета.
        </p>
      </div>
    );
  }

  // Найти информацию о выбранном типе очистки
  const waterTypeInfo = waterTypes.find(type => type.id === formData.waterType) || { name: 'Стандартная', maintenanceCost: 1.0 };
  
  // Найти информацию о выбранной системе фильтрации
  const filterSystemInfo = filterSystems.find(system => system.id === formData.filterSystem) || { name: 'Песочный фильтр', maintenanceFactor: 1.0, replacementYears: 5 };
  
  // Расчет потребности химических веществ
  const chlorineWeekly = results.chemicalRecommendation?.chlorineWeekly || 0;
  const phRegulatorMonthly = results.chemicalRecommendation?.phRegulatorMonthly || 0;
  const algaecideMonthly = results.chemicalRecommendation?.algaecideMonthly || 0;
  
  // Данные по примерным ценам в России (2025 год)
  const priceData = {
    chlorine: 550, // руб/кг
    phRegulator: 350, // руб/кг
    algaecide: 1200, // руб/л
    waterTest: 150, // руб/тест
    filterMedia: formData.filterSystem === 'sand' ? 3500 : 
                formData.filterSystem === 'cartridge' ? 2500 : 15000, // руб за замену
    solarCover: 2500, // руб/м²
    electricity: 5.5, // руб/кВт*ч (средний тариф по России)
    water: 40 // руб/м³ (средний тариф по России)
  };
  
  // Расчет годовых затрат на химию
  const chemicalCostAnnual = (
    chlorineWeekly * priceData.chlorine * 52 + // 52 недели в году
    phRegulatorMonthly * priceData.phRegulator * 12 + // 12 месяцев
    algaecideMonthly * priceData.algaecide * 12
  ) * (formData.poolLocation === 'indoor' ? 1 : parseFloat(formData.seasonLength) / 12);
  
  // Расчет затрат на замену фильтрующих элементов (в год)
  const filterMediaCostAnnual = priceData.filterMedia / filterSystemInfo.replacementYears;
  
  // Расчет затрат на воду (для первичного заполнения и подпитки)
  const waterCostInitial = results.netVolume * priceData.water;
  const waterCostAnnual = results.waterConsumptionAnnual * priceData.water;
  
  // Расчет затрат на электроэнергию (приблизительно)
  const pumpPower = results.filterRecommendation.pumpPower; // кВт
  const pumpHoursDaily = 8; // часов в день
  const electricityCostAnnual = pumpPower * pumpHoursDaily * priceData.electricity * 30 * 
                              (formData.poolLocation === 'indoor' ? 12 : parseFloat(formData.seasonLength));
  
  // Суммарные годовые затраты на обслуживание
  const totalMaintenanceCostAnnual = chemicalCostAnnual + filterMediaCostAnnual + 
                                  waterCostAnnual + electricityCostAnnual;
  
  // Определение рекомендаций для данного бассейна
  const getRecommendations = () => {
    const recommendations = [];
    
    // Базовые рекомендации в зависимости от объема
    if (results.netVolume < 10) {
      recommendations.push("Для небольшого объема используйте комбинированные препараты для упрощения ухода");
    } else if (results.netVolume > 50) {
      recommendations.push("Для больших объемов рекомендуется автоматическая станция дозирования химии");
    }
    
    // Рекомендации по фильтрации
    if (results.netVolume > 20 && formData.filterSystem === 'cartridge') {
      recommendations.push("Для вашего объема эффективнее использовать песочный фильтр вместо картриджного");
    }
    
    // Рекомендации по климату и расположению
    if (formData.climate === 'hot' && formData.poolLocation === 'outdoor') {
      recommendations.push("В жарком климате увеличьте дозировку альгицида на 20-30%");
    }
    
    // Рекомендации по экономии
    if (formData.poolLocation === 'outdoor' && formData.coverType === 'none') {
      recommendations.push("Используйте солнечное покрытие для снижения испарения и экономии на нагреве");
    }
    
    // Если рекомендаций нет, добавляем стандартную
    if (recommendations.length === 0) {
      recommendations.push("Регулярно проверяйте уровень pH и хлора для оптимального качества воды");
    }
    
    return recommendations;
  };
  
  // Получаем рекомендации
  const recommendations = getRecommendations();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ShowerHead className="w-5 h-5 mr-2 text-cyan-500" />
        Обслуживание воды
      </h3>
      
      {/* Основная информация */}
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4 border border-cyan-100 dark:border-cyan-800">
          <h4 className="text-sm font-medium text-cyan-800 dark:text-cyan-300 mb-2 flex items-center">
            <Beaker className="w-4 h-4 mr-1.5" />
            Расход химии
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Хлор еженедельно:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(chlorineWeekly, 2)} кг</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">pH-регулятор ежемесячно:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(phRegulatorMonthly, 2)} кг</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Альгицид ежемесячно:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(algaecideMonthly, 2)} л</span>
            </div>
          </div>
        </div>
        
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800">
          <h4 className="text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-2 flex items-center">
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Циркуляция воды
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Оптимальный проток:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.filterRecommendation.circulationRate, 1)} м³/ч</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Рекомендуемая мощность насоса:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.filterRecommendation.pumpPower, 1)} кВт</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Время цикла фильтрации:</span>
              <span className="font-medium text-gray-900 dark:text-white">6-8 часов/день</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Затраты на обслуживание */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Примерные затраты на обслуживание:
        </h4>
        
        <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Категория расходов</span>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Химия для воды</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(chemicalCostAnnual, 0)}/год</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Замена фильтров</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(filterMediaCostAnnual, 0)}/год</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Расход воды</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(waterCostAnnual, 0)}/год</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Электроэнергия</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(electricityCostAnnual, 0)}/год</span>
            </div>
            <div className="flex justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Общая стоимость</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(totalMaintenanceCostAnnual, 0)}/год</span>
            </div>
          </div>
        </div>
        
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          * Расчеты основаны на средних ценах по России на {new Date().getFullYear()} год
        </p>
      </div>
      
      {/* Рекомендации */}
      <div>
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <AlertTriangle className="w-4 h-4 mr-1.5 text-amber-500" />
          Рекомендации для вашего бассейна:
        </h4>
        
        <ul className="space-y-2">
          {recommendations.map((recommendation, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{recommendation}</span>
            </li>
          ))}
        </ul>
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            При {formData.poolLocation === 'outdoor' ? 'сезонном' : 'круглогодичном'} использовании бассейна объемом {formatNumber(results.netVolume, 1)} м³ 
            с {waterTypeInfo.name.toLowerCase()} системой очистки рекомендуется регулярная проверка 
            параметров воды 1-2 раза в неделю и профилактическая очистка чаши бассейна каждые 2-3 месяца.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaterMaintenanceSidebar;